import { useState, useEffect } from 'react';
import { IconButton, Menu, MenuItem, Avatar, Divider, ListItemIcon, ListItemText, Badge, Box } from '@mui/material';
import { 
  Logout as LogoutIcon, 
  Settings as SettingsIcon, 
  AdminPanelSettings as AdminIcon,
  Mail as MailIcon,
  Dashboard as DashboardIcon,
  Person as ProfileIcon
} from '@mui/icons-material';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { getUnreadCountForUser, subscribeToUserMessages } from '../../utils/messageService';

function UserMenu({ user, userProfile, onNavigate }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!user) return;

    // Subscribe to messages for real-time unread count
    const unsubscribe = subscribeToUserMessages(user.uid, (messages) => {
      // Count messages where admin replied AFTER user last read
      const unread = messages.filter((msg) => {
        if (msg.status === 'resolved') return false; // Skip resolved
        
        if (msg.messages && msg.messages.length > 0) {
          const lastMessage = msg.messages[msg.messages.length - 1];
          
          // Only count if last message is from admin
          if (lastMessage.from === 'admin') {
            const lastMessageTime = new Date(lastMessage.timestamp).getTime();
            const lastReadTime = msg.lastReadByUser 
              ? (msg.lastReadByUser.toDate ? msg.lastReadByUser.toDate().getTime() : new Date(msg.lastReadByUser).getTime())
              : 0;
            
            // Unread if admin message is newer than last read time
            return lastMessageTime > lastReadTime;
          }
        }
        return false;
      }).length;
      setUnreadCount(unread);
    });

    return () => unsubscribe();
  }, [user]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await signOut(auth);
      console.log('👋 User logged out');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  const handleSettings = () => {
    handleClose();
    onNavigate('/settings');
  };

  const handleAdmin = () => {
    handleClose();
    onNavigate('/admin');
  };

  const handleInbox = () => {
    handleClose();
    onNavigate('/inbox');
  };

  const handleDashboard = () => {
    handleClose();
    onNavigate('/dashboard');
  };

  if (!user) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {/* Mail Icon with Badge */}
      <IconButton
        onClick={handleInbox}
        sx={{ ml: 1 }}
        aria-label="inbox"
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              boxShadow: '0 0 10px rgba(239,68,68,0.6)',
            }
          }}
        >
          <MailIcon sx={{ color: 'primary.main' }} />
        </Badge>
      </IconButton>

      {/* User Avatar */}
      <IconButton
        onClick={handleClick}
        sx={{ ml: 1 }}
        aria-label="account menu"
      >
        <Avatar 
          src={user.photoURL} 
          alt={user.displayName || user.email}
          sx={{ 
            width: 32, 
            height: 32,
            bgcolor: 'background.paper',
            color: 'primary.main',
            fontSize: '0.875rem',
            border: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          {(user.displayName || user.email || '?')[0].toUpperCase()}
        </Avatar>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            mt: 1.5,
            minWidth: 200,
            boxShadow: '0 0 20px rgba(0,255,245,0.2)',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          disabled
          sx={{ 
            opacity: 1,
            '&.Mui-disabled': {
              opacity: 1
            }
          }}
        >
          <ListItemText 
            primary={user.displayName || 'User'}
            secondary={user.email}
            primaryTypographyProps={{
              sx: { 
                color: 'text.primary',
                fontSize: '0.875rem',
                fontWeight: 600
              }
            }}
            secondaryTypographyProps={{
              sx: { 
                color: 'text.secondary',
                fontSize: '0.75rem'
              }
            }}
          />
        </MenuItem>
        
        <Divider sx={{ borderColor: 'divider', my: 1 }} />
        
        <MenuItem 
          onClick={() => {
            handleClose();
            onNavigate(`/profiles/${user.uid}_career`);
          }}
          sx={{
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'rgba(0,255,245,0.1)'
            }
          }}
        >
          <ListItemIcon>
            <ProfileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText 
            primary="My Profile"
            primaryTypographyProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        
        <MenuItem 
          onClick={handleDashboard}
          sx={{
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'rgba(0,255,245,0.1)'
            }
          }}
        >
          <ListItemIcon>
            <DashboardIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Career Builder"
            primaryTypographyProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        
        <MenuItem 
          onClick={handleInbox}
          sx={{
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'rgba(0,255,245,0.1)'
            }
          }}
        >
          <ListItemIcon>
            <Badge badgeContent={unreadCount} color="error">
              <MailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            </Badge>
          </ListItemIcon>
          <ListItemText 
            primary="Inbox"
            primaryTypographyProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        
        {userProfile?.role === 'admin' && (
          <>
            <MenuItem 
              onClick={handleAdmin}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'rgba(157,0,255,0.1)'
                }
              }}
            >
              <ListItemIcon>
                <AdminIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Admin Dashboard"
                primaryTypographyProps={{
                  sx: { fontSize: '0.875rem' }
                }}
              />
            </MenuItem>
          </>
        )}
        
        <MenuItem 
          onClick={handleSettings}
          sx={{
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'rgba(0,255,245,0.1)'
            }
          }}
        >
          <ListItemIcon>
            <SettingsIcon sx={{ color: 'text.primary', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Settings"
            primaryTypographyProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        
        <Divider sx={{ borderColor: 'divider', my: 1 }} />
        
        <MenuItem 
          onClick={handleLogout}
          sx={{
            color: 'error.main',
            '&:hover': {
              bgcolor: 'rgba(239,68,68,0.1)'
            }
          }}
        >
          <ListItemIcon>
            <LogoutIcon sx={{ color: 'error.main', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Log Out"
            primaryTypographyProps={{
              sx: { fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default UserMenu;

